/**
 * LongevityEngine.jsx — Iter-366 (Feb 2026).
 *
 * Pure classification helpers for the Longevity / Biometric Journey
 * Vault. Keep this file free of React state, network calls, and side
 * effects — consumer surfaces (`LongevityTab.jsx` on the staff side,
 * `LongevityDashboard.jsx` on the guest side) import from here and own
 * their own rendering.
 *
 * Iter-367 (Feb 2026) — Access Whitelist. The whole feature is
 * gated behind a hard-coded email allowlist so we can dogfood the
 * Longevity module with a small circle before opening it up to the
 * general Member base. The engine owns the truth (`isLongevityUnlocked`),
 * consumer surfaces call it, and the backend mirrors the same rules on
 * every route. Test environments bypass via env (see
 * `LONGEVITY_BYPASS_WHITELIST=1` in `backend/tests/conftest.py`).
 *
 * Isolation contract (matches the 8 sealed engines):
 *   - No `useState` / `useEffect`.
 *   - No `axios` / `fetch` / DOM globals.
 *   - No inline JSX components (constants + pure functions only).
 *
 * Engine boundary: this file is NEW in Iter-366 and joins the roster
 * of the 8 sealed engines. Consumer imports are stable — never rename
 * an exported symbol without a coordinated refactor.
 */

// ---------- Access Whitelist (Iter-367) ----------
// Explicit personal / product-owner accounts.
export const LONGEVITY_ALLOWED_EMAILS = Object.freeze([
  "setiawan.lauw@gmail.com",
  "louisecogan@hotmail.com",
]);

// Domain-suffix allowlist (case-insensitive). `@emergent.sh` covers the
// Emergent team for system testing; `@example.com` covers the pytest
// sandbox seed defaults so backend `conftest.py`-driven runs stay green
// without any conditional test seeding.
export const LONGEVITY_ALLOWED_DOMAINS = Object.freeze([
  "@emergent.sh",
  "@example.com",
]);

/** Pure whitelist gate. Returns `true` when the email belongs to the
 *  Longevity access ring (personal owner OR whitelisted domain). Empty
 *  / null / undefined always returns `false` — a signed-out surface
 *  MUST render as if the feature does not exist. */
export function isLongevityUnlocked(email) {
  const e = String(email || "").trim().toLowerCase();
  if (!e) return false;
  if (LONGEVITY_ALLOWED_EMAILS.includes(e)) return true;
  return LONGEVITY_ALLOWED_DOMAINS.some((d) => e.endsWith(d));
}

// ---------- Metric definitions ----------
export const BODY_COMPOSITION_FIELDS = Object.freeze([
  { key: "weight_kg", label: "Weight", unit: "kg", min: 20, max: 400, decimals: 1, higherIsBetter: null },
  { key: "muscle_mass_kg", label: "Muscle Mass", unit: "kg", min: 1, max: 200, decimals: 1, higherIsBetter: true },
  { key: "fat_percent", label: "Body Fat", unit: "%", min: 0, max: 80, decimals: 1, higherIsBetter: false },
  { key: "biological_age", label: "Biological Age", unit: "yrs", min: 5, max: 120, decimals: 1, higherIsBetter: false },
]);

export const PERFORMANCE_FIELDS = Object.freeze([
  { key: "vo2_max", label: "VO2 Max", unit: "ml/kg/min", min: 5, max: 100, decimals: 1, higherIsBetter: true },
]);

export const WELLNESS_FIELDS = Object.freeze([
  { key: "psychological", label: "Psychological Assessment", unit: "/100", min: 0, max: 100, decimals: 0, higherIsBetter: true, noteKey: "psychological_note" },
  { key: "cognitive", label: "Cognitive Assessment", unit: "/100", min: 0, max: 100, decimals: 0, higherIsBetter: true, noteKey: "cognitive_note" },
]);

export const SUGGESTED_BLOOD_MARKERS = Object.freeze([
  "HbA1c", "Fasting Glucose", "Total Cholesterol", "HDL", "LDL",
  "Triglycerides", "hs-CRP", "TSH", "Vitamin D (25-OH)", "Vitamin B12",
  "Ferritin", "Testosterone (Total)",
]);

// ---------- Validation ----------
export function coerceNumber(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function validateField(field, raw) {
  const n = coerceNumber(raw);
  if (n === null) return { ok: true, value: null }; // empty is valid — every section is optional
  if (n < field.min || n > field.max) {
    return { ok: false, error: `${field.label} must be between ${field.min} and ${field.max} ${field.unit}` };
  }
  return { ok: true, value: n };
}

export function validateBloodMarker(marker) {
  const name = (marker?.name || "").trim();
  const value = coerceNumber(marker?.value);
  if (!name && value === null) return { ok: true, empty: true };
  if (!name) return { ok: false, error: "Blood marker name required." };
  if (value === null) return { ok: false, error: `Value for ${name} is required.` };
  return { ok: true, empty: false, value: { name, value, unit: (marker.unit || "").trim(), reference_range: marker.reference_range || null } };
}

/** Consolidates form state into the payload the backend expects.
 *  Sections are omitted entirely when the whole section is blank so
 *  a partial update never overwrites a section the doctor did not
 *  touch. */
export function buildEntryPayload(form) {
  const payload = { notes: (form.notes || "").trim() };

  const bc = {};
  for (const f of BODY_COMPOSITION_FIELDS) {
    const v = coerceNumber(form.body_composition?.[f.key]);
    if (v !== null) bc[f.key] = v;
  }
  if (Object.keys(bc).length > 0) payload.body_composition = bc;

  const perf = {};
  for (const f of PERFORMANCE_FIELDS) {
    const v = coerceNumber(form.physical_performance?.[f.key]);
    if (v !== null) perf[f.key] = v;
  }
  if (Object.keys(perf).length > 0) payload.physical_performance = perf;

  const well = {};
  for (const f of WELLNESS_FIELDS) {
    const v = coerceNumber(form.wellness_scores?.[f.key]);
    if (v !== null) well[f.key] = v;
    const note = (form.wellness_scores?.[f.noteKey] || "").trim();
    if (note) well[f.noteKey] = note;
  }
  if (Object.keys(well).length > 0) payload.wellness_scores = well;

  const blood = [];
  for (const m of (form.blood_metrics || [])) {
    const v = validateBloodMarker(m);
    if (v.ok && !v.empty) blood.push(v.value);
  }
  if (blood.length > 0) payload.blood_metrics = blood;

  if (form.recorded_at) payload.recorded_at = form.recorded_at;
  if (form.branch_id) payload.branch_id = form.branch_id;
  return payload;
}

// ---------- Formatting ----------
export function formatMetric(field, value) {
  const n = coerceNumber(value);
  if (n === null) return "—";
  const d = field.decimals ?? 1;
  return `${n.toFixed(d)} ${field.unit}`.trim();
}

export function formatDelta(field, current, previous) {
  const cur = coerceNumber(current);
  const prev = coerceNumber(previous);
  if (cur === null || prev === null) return null;
  const delta = cur - prev;
  if (Math.abs(delta) < 0.05) return { sign: "flat", label: "no change" };
  const positive = delta > 0;
  const good = field.higherIsBetter === null
    ? null
    : (positive ? field.higherIsBetter : !field.higherIsBetter);
  const d = field.decimals ?? 1;
  return {
    sign: positive ? "up" : "down",
    good,
    label: `${positive ? "+" : ""}${delta.toFixed(d)} ${field.unit}`.trim(),
  };
}

export function formatEntryDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

/** Returns the most recent + previous snapshot for delta rendering.
 *  Assumes `entries` is already sorted newest-first (matches the
 *  backend `.sort("recorded_at", DESCENDING)` contract). */
export function selectLatestPair(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return { latest: list[0] || null, previous: list[1] || null };
}

// ---------------------------------------------------------------------------
// Iter-370 — Longevity Journey Onboarding Wizard
// ---------------------------------------------------------------------------
// Ordered step labels rendered by `LongevityOnboarding.jsx`. Length ==
// total steps → the wizard shell derives its progress bar + "Step N of
// M" copy from this list, so adding / renaming a step only needs an
// edit here + the matching case block in the shell.
export const LONGEVITY_ONBOARDING_STEPS = Object.freeze([
  "Consent Information",
  "Your Identity",
  "Personal Context",
  "Body & Ancestry",
  "Gut Health",
  "Mind & Energy",
  "Lifestyle & Environment",
  "Your Goals",
  "Documents",
  "Review & Submit",
]);

// Structured option lists that hydrate the multi-check + dropdown
// widgets inside the wizard steps. Kept here so the engine owns the
// canonical vocabulary — surfaces MUST NOT hard-code these.
export const DIAGNOSES_OPTIONS = Object.freeze([
  "Type 1 Diabetes",
  "Type 2 Diabetes",
  "High Blood Pressure",
  "High Cholesterol",
  "Heart Disease",
  "Stroke",
  "Cancer",
  "Autoimmune Disease",
  "Thyroid Disease",
  "Kidney Disease",
  "Liver Disease",
  "Osteoporosis",
  "Arthritis",
  "Anxiety / Depression",
  "Asthma",
  "None",
  "Other",
]);

export const SYMPTOMS_OPTIONS = Object.freeze([
  "Fatigue",
  "Brain Fog",
  "Headaches",
  "Muscle Pain",
  "Joint Pain",
  "Digestive Issues",
  "Sleep Issues",
  "Anxiety",
  "Low Mood",
  "Skin Issues",
  "Weight Changes",
  "Hair Loss",
  "Low Libido",
  "Menstrual Issues",
  "Other",
]);

export const ANCESTRY_OPTIONS = Object.freeze([
  "European",
  "East Asian",
  "South Asian",
  "Southeast Asian",
  "African",
  "Middle Eastern",
  "Latin American",
  "Indigenous / First Nations",
  "Pacific Islander",
  "Mixed",
  "Prefer not to say",
]);

export const GUT_INVESTIGATION_OPTIONS = Object.freeze([
  "Colonoscopy",
  "Gastroscopy",
  "Stool Test (parasites, calprotectin)",
  "Microbiome Test",
  "SIBO Breath Test",
  "Food Sensitivity Test",
  "H. pylori Test",
  "None",
]);

export const REGULAR_DIET_OPTIONS = Object.freeze([
  "Soda / Soft Drinks",
  "Sweet Treats",
  "Gluten (bread, pasta)",
  "Dairy",
  "Red Meat",
  "Fish",
  "Chicken",
  "Eggs",
  "Legumes",
  "Nuts & Seeds",
  "Fresh Vegetables",
  "Fresh Fruit",
  "Fermented Foods",
  "Processed / Packaged Foods",
  "Fast Food",
  "Coffee",
  "Tea",
]);

export const EATING_PATTERNS_OPTIONS = Object.freeze([
  "Keto",
  "Low-Carb",
  "Vegan",
  "Vegetarian",
  "Pescatarian",
  "Paleo",
  "Mediterranean",
  "Intermittent Fasting",
  "Extended Fasting",
  "Calorie Counting",
  "None",
]);

export const RECREATIONAL_DRUGS_OPTIONS = Object.freeze([
  "Cannabis",
  "MDMA",
  "Cocaine",
  "Psychedelics (LSD, Psilocybin)",
  "Ketamine",
  "Opioids",
  "Amphetamines",
  "Other",
  "None",
]);

// ---------- Empty form scaffold ----------
export function emptyEntryForm() {
  return {
    recorded_at: "",
    branch_id: "",
    notes: "",
    body_composition: { weight_kg: "", muscle_mass_kg: "", fat_percent: "", biological_age: "" },
    physical_performance: { vo2_max: "" },
    wellness_scores: { psychological: "", cognitive: "", psychological_note: "", cognitive_note: "" },
    blood_metrics: [{ name: "", value: "", unit: "", reference_range: "" }],
  };
}

// ---------------------------------------------------------------------------
// Biometric Vault — longitudinal records (Feb 2026)
// ---------------------------------------------------------------------------
// Field catalogs rendered by `BiometricVault.jsx` (shared by the guest
// portal + staff tab). All numeric fields support the same empty →
// null coercion as `coerceNumber()` above.

export const BIOMETRIC_BODY_FIELDS = Object.freeze([
  { key: "weight_kg", label: "Weight", unit: "kg", min: 20, max: 400, decimals: 1 },
  { key: "body_fat_percent", label: "Body Fat", unit: "%", min: 0, max: 80, decimals: 1 },
  { key: "muscle_mass_kg", label: "Muscle Mass", unit: "kg", min: 1, max: 200, decimals: 1 },
  { key: "visceral_fat", label: "Visceral Fat", unit: "", min: 0, max: 100, decimals: 1 },
]);

export const BIOMETRIC_BLOOD_FIELDS = Object.freeze([
  { key: "glucose", label: "Fasting Glucose", unit: "mg/dL", min: 0, max: 1000, decimals: 1 },
  { key: "cholesterol", label: "Cholesterol", unit: "mg/dL", min: 0, max: 1000, decimals: 1 },
  { key: "hba1c", label: "HbA1c", unit: "%", min: 0, max: 20, decimals: 2 },
  { key: "vitamin_d", label: "Vitamin D", unit: "ng/mL", min: 0, max: 500, decimals: 1 },
  { key: "ferritin", label: "Ferritin", unit: "ng/mL", min: 0, max: 5000, decimals: 1 },
]);

export const BIOMETRIC_VO2_FIELD = Object.freeze({
  key: "vo2_max", label: "VO2 Max", unit: "ml/kg/min", min: 5, max: 100, decimals: 1,
});

export const BIOMETRIC_SLEEP_FIELD = Object.freeze({
  key: "sleep_score", label: "Sleep Score", unit: "/100", min: 0, max: 100, decimals: 0,
});

export const PHASE_LABELS = Object.freeze({
  before_first_visit: "Before First Visit",
  during_stay: "During Stay",
  after_checkout: "After Checkout",
});

export const PHASE_ORDER = Object.freeze([
  "before_first_visit",
  "during_stay",
  "after_checkout",
]);

/** Returns an array of `{phase, label, entries}` in canonical order.
 *  Entries inside each phase keep the incoming (newest-first) order. */
export function groupBiometricEntriesByPhase(entries) {
  const buckets = { before_first_visit: [], during_stay: [], after_checkout: [] };
  for (const e of entries || []) {
    const p = e?.phase && buckets[e.phase] ? e.phase : "after_checkout";
    buckets[p].push(e);
  }
  return PHASE_ORDER.map((p) => ({ phase: p, label: PHASE_LABELS[p], entries: buckets[p] }));
}

/** Extract an oldest-first `{date, value}` series for charting. The
 *  `path` is dot-separated — e.g. `body_composition.weight_kg`,
 *  `blood_results.hba1c`, `vo2_max`, `biological_age`. Non-numeric
 *  values are skipped so the chart renders only valid points. */
export function extractBiometricSeries(entries, path) {
  const parts = String(path || "").split(".");
  const sorted = (entries || []).slice().sort((a, b) => (a.recorded_at > b.recorded_at ? 1 : -1));
  const out = [];
  for (const e of sorted) {
    let v = e;
    for (const p of parts) v = v?.[p];
    if (typeof v === "number" && Number.isFinite(v)) {
      out.push({ date: e.recorded_at, value: v, actor: e.actor?.name || "" });
    }
  }
  return out;
}

/** Short date label used on chart ticks and timeline rows. */
export function shortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

/** Rich date + time label for history rows. */
export function longDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

/** Minimum metric map for the Biometric Vault add-entry form. */
export function emptyBiometricForm() {
  return {
    body_composition: { weight_kg: "", body_fat_percent: "", muscle_mass_kg: "", visceral_fat: "" },
    vo2_max: "",
    sleep_score: "",
    blood_results: { glucose: "", cholesterol: "", hba1c: "", vitamin_d: "", ferritin: "" },
  };
}

/** Shape form state into the backend payload. Blank fields are dropped
 *  so the backend never sees empty strings. `includeBlood=false` for
 *  the guest path (defence-in-depth; the server strips anyway). */
export function buildBiometricPayload(form, { includeBlood = false } = {}) {
  const payload = {};
  const bc = {};
  for (const f of BIOMETRIC_BODY_FIELDS) {
    const v = coerceNumber(form?.body_composition?.[f.key]);
    if (v !== null) bc[f.key] = v;
  }
  if (Object.keys(bc).length > 0) payload.body_composition = bc;

  const vo2 = coerceNumber(form?.vo2_max);
  if (vo2 !== null) payload.vo2_max = vo2;

  const sleep = coerceNumber(form?.sleep_score);
  if (sleep !== null) payload.sleep_score = sleep;

  if (includeBlood) {
    const br = {};
    for (const f of BIOMETRIC_BLOOD_FIELDS) {
      const v = coerceNumber(form?.blood_results?.[f.key]);
      if (v !== null) br[f.key] = v;
    }
    if (Object.keys(br).length > 0) payload.blood_results = br;
  }
  return payload;
}

/** The guest/staff form must carry at least one measurable field. */
export function biometricPayloadHasData(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (payload.body_composition && Object.keys(payload.body_composition).length) return true;
  if (typeof payload.vo2_max === "number") return true;
  if (typeof payload.sleep_score === "number") return true;
  if (payload.blood_results && Object.keys(payload.blood_results).length) return true;
  return false;
}

/** Format a biological-age delta vs actual age into a human sentence. */
export function describeBiologicalAge(entry) {
  if (!entry || entry.biological_age == null) return null;
  const delta = Number(entry.biological_age_delta ?? 0);
  const bio = entry.biological_age;
  if (delta === 0) return `Biological Age: ${bio} — matches your actual age`;
  const years = Math.abs(delta);
  const dir = delta < 0 ? "younger" : "older";
  const noun = years === 1 ? "year" : "years";
  return `Biological Age: ${bio} — ${years} ${noun} ${dir} than your actual age`;
}
